import { useMemo, type FC } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { Button, DataTable, Input, Select } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import type { StaffRegulator } from "../../../queries/staff-regulators/types";
import { STAFF_REGULATORS_NEW_ROUTE } from "../staffRegulatorsRoutes";
import { buildStaffRegulatorProfilePath } from "../staffRegulatorsRoutes";
import { staffRegulatorDisplayName } from "../staffRegulatorsData";
import StaffRegulatorStatusBadge from "../StaffRegulatorStatusBadge/StaffRegulatorStatusBadge";

import styles from "./StaffRegulatorsList.module.css";

export type StaffRegulatorsListFilters = {
  search: string;
  onSearchChange: (next: string) => void;
  status: StaffRegulator["status"] | undefined;
  onStatusChange: (next: StaffRegulator["status"] | undefined) => void;
};

export type StaffRegulatorsListProps = {
  rows: StaffRegulator[];
  loading: boolean;
  filters: StaffRegulatorsListFilters;
};

const StaffRegulatorsList: FC<StaffRegulatorsListProps> = ({
  rows,
  loading,
  filters,
}) => {
  const navigate = useNavigate();

  const columns = useMemo<Array<Record<string, unknown>>>(
    () => [
      {
        title: AppStrings.ID,
        dataIndex: "admin_id",
        key: "admin_id",
        width: 160,
      },
      {
        title: AppStrings.NAME,
        key: "name",
        width: 180,
        render: (_value: unknown, record: StaffRegulator) => (
          <button
            type="button"
            className={styles.nameLink}
            onClick={() =>
              navigate(buildStaffRegulatorProfilePath(record.admin_id))
            }
          >
            {staffRegulatorDisplayName(record)}
          </button>
        ),
      },
      {
        title: AppStrings.EMAIL,
        dataIndex: "email",
        key: "email",
        width: 220,
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (value: StaffRegulator["status"]) =>
          value ? <StaffRegulatorStatusBadge status={value} /> : null,
      },
    ],
    [navigate],
  );

  return (
    <>
      <div className={styles.searchSection}>
        <div className={styles.searchInputs}>
          <Input
            variant="search"
            className={styles.searchInput}
            placeholder={AppStrings.COMPLIANCE_REGULATORS_SEARCH}
            value={filters.search}
            onChange={(e) => filters.onSearchChange(e.target.value)}
            allowClear
          />
          <Select<StaffRegulator["status"]>
            className={styles.statusFilter}
            placeholder={AppStrings.STATUS}
            value={filters.status}
            onChange={(value) => filters.onStatusChange(value)}
            allowClear
            options={[
              {
                value: "pending",
                label: <StaffRegulatorStatusBadge status="pending" />,
              },
              {
                value: "approved",
                label: <StaffRegulatorStatusBadge status="approved" />,
              },
            ]}
          />
        </div>
        <div className={styles.searchActions}>
          <Button
            size="large"
            onClick={() => navigate(STAFF_REGULATORS_NEW_ROUTE)}
          >
            <PlusOutlined className={styles.newIcon} />
            <span>{AppStrings.COMPLIANCE_REGULATORS_NEW}</span>
          </Button>
        </div>
      </div>

      <div className={styles.tableSection}>
        <DataTable<StaffRegulator>
          rowKey="admin_id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
        />
      </div>
    </>
  );
};

export default StaffRegulatorsList;
